/**
 * Orders Page (Placeholder)
 * Will be fully implemented in Phase 2
 */

import { useEffect, useState } from 'react';
import { useOrdersStore } from '@/store/orders';
import { useOrderUpdates } from '@/hooks/useOrderUpdates';
import { useToastStore } from '@/store/toasts';
import { wsClient } from '@/lib/ws';
import { logger } from '@/lib/logger';
import OrderCard from '@/components/orders/OrderCard';
import ActiveOrderCard from '@/components/orders/ActiveOrderCard';

export default function OrdersPage() {
  const {
    newOrders,
    activeOrders,
    fetchOrders,
    acceptOrder,
    rejectOrder,
    markOrderStatus,
    isLoading,
  } = useOrdersStore();

  // Real-time order updates
  const { connectionState } = useOrderUpdates();
  
  // Toast notifications (Sprint 27)
  const addToast = useToastStore((state) => state.addToast);
  
  // Connection state for visual indicator
  const [_isConnected, setIsConnected] = useState(false);
  const [activeTab, setActiveTab] = useState<'new' | 'active'>('new');
  const [acceptingOrderId, setAcceptingOrderId] = useState<string | null>(null);
  const [rejectingOrderId, setRejectingOrderId] = useState<string | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Check WebSocket connection status every second
  useEffect(() => {
    const checkConnection = () => {
      setIsConnected(wsClient.isConnected());
    };

    // Initial check
    checkConnection();

    // Poll every second
    const interval = setInterval(checkConnection, 1000);

    return () => clearInterval(interval);
  }, []);

  // Sprint 27: Helper to extract error message
  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
      const err = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
      if (err.response?.data?.error?.message) {
        return err.response.data.error.message;
      }
      if (err.message) {
        return err.message;
      }
    }
    return 'An unexpected error occurred';
  };

  const handleAccept = async (orderId: string) => {
    setAcceptingOrderId(orderId);
    try {
      await acceptOrder(orderId, 15); // 15 minutes prep time
      logger.info('Order accepted successfully', { orderId });
      addToast({ type: 'success', message: 'Order accepted' });
    } catch (error) {
      const message = getErrorMessage(error);
      logger.error('Failed to accept order', error as Error, { orderId });
      addToast({ type: 'error', message: `Failed to accept order: ${message}` });
    } finally {
      setAcceptingOrderId(null);
    }
  };

  const handleReject = async (orderId: string) => {
    setRejectingOrderId(orderId);
    try {
      await rejectOrder(orderId, 'Vendor declined order');
      logger.info('Order rejected successfully', { orderId });
      addToast({ type: 'success', message: 'Order rejected' });
    } catch (error) {
      const message = getErrorMessage(error);
      logger.error('Failed to reject order', error as Error, { orderId });
      addToast({ type: 'error', message: `Failed to reject order: ${message}` });
    } finally {
      setRejectingOrderId(null);
    }
  };

  const handleUpdateStatus = async (orderId: string, status: 'preparing' | 'ready_for_pickup') => {
    setUpdatingOrderId(orderId);
    try {
      await markOrderStatus(orderId, status);
      logger.info('Order status updated successfully', { orderId, status });
      const statusLabel = status === 'preparing' ? 'Preparing' : 'Ready for Pickup';
      addToast({ type: 'success', message: `Order marked as ${statusLabel}` });
    } catch (error) {
      const message = getErrorMessage(error);
      logger.error('Failed to update order status', error as Error, { orderId, status });
      addToast({ type: 'error', message: `Failed to update status: ${message}` });
    } finally {
      setUpdatingOrderId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Orders</h1>
              <p className="text-muted-foreground">Manage incoming and active orders</p>
            </div>

            {/* Connection Status */}
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                connectionState === 'connected'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : connectionState === 'connecting'
                  ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                  : 'bg-gray-50 text-gray-700 border border-gray-200'
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  connectionState === 'connected' 
                    ? 'bg-green-500' 
                    : connectionState === 'connecting'
                    ? 'bg-yellow-500 animate-pulse'
                    : 'bg-gray-400'
                }`}
              />
              <span className="text-sm font-medium">
                {connectionState === 'connected' ? 'Live' : connectionState === 'connecting' ? 'Connecting...' : 'Offline'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('new')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'new'
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            New Orders ({newOrders.length})
            {activeTab === 'new' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeTab === 'active'
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Active Orders ({activeOrders.length})
            {activeTab === 'active' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading orders...</p>
          </div>
        ) : (
          <>
            {/* New Orders Tab */}
            {activeTab === 'new' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {newOrders.length === 0 ? (
                  <div className="col-span-full bg-card border border-border rounded-xl p-12 text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No New Orders
                    </h3>
                    <p className="text-muted-foreground">
                      New orders will appear here automatically when customers place them
                    </p>
                  </div>
                ) : (
                  newOrders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onAccept={handleAccept}
                      onReject={handleReject}
                      isAccepting={acceptingOrderId === order.id}
                      isRejecting={rejectingOrderId === order.id}
                    />
                  ))
                )}
              </div>
            )}

            {/* Active Orders Tab */}
            {activeTab === 'active' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activeOrders.length === 0 ? (
                  <div className="col-span-full bg-card border border-border rounded-xl p-12 text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No Active Orders
                    </h3>
                    <p className="text-muted-foreground">
                      Accepted orders will appear here for preparation and tracking
                    </p>
                  </div>
                ) : (
                  activeOrders.map((order) => (
                    <ActiveOrderCard
                      key={order.id}
                      order={order}
                      onUpdateStatus={(status) => handleUpdateStatus(order.id, status)}
                      isUpdatingStatus={updatingOrderId === order.id}
                    />
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

